import cloneDeep from 'lodash/cloneDeep';
import moment from 'moment-timezone';
import { deepClone } from '@mui/x-data-grid/internals';
import { useAppSelector } from '../../../../hooks/useAppStore';
import {
  getChannelData,
  getEmployeeRates,
  getLocationStats,
  getPromotersTotals,
} from '../../../../store/orders/selectors';
import {
  TAssignOrder, TMember, TPromoterTotal, TTimesheetsList,
} from '../../../../types';
import { useCommon } from '../../../../hooks/useCommon';
import { getAuthenticateUser } from '../../../../store/authenticate/selectors';
import { getMembers } from '../../../../store/team/selectors';
import { getSheets } from '../../../../store/sheets/selectors';
import { isInRangeMoment } from '../../../../utils/helpers';
import { calculateOrderCommissionByPlan } from '../../../../utils/helpers/orders.helper';

export function useStats() {
  const employeeRates = useAppSelector(getEmployeeRates);
  const { locationsWithColors, locationsByPermissions } = useCommon();
  const channelData = useAppSelector(getChannelData);
  const locationStats = useAppSelector(getLocationStats);
  const { shop } = useAppSelector(getAuthenticateUser);
  const members = useAppSelector(getMembers);
  const sheets = useAppSelector(getSheets);
  const promotersTotal = useAppSelector(getPromotersTotals);

  const aggregateDataByLocation = (
    orders: Array<TAssignOrder>,
  ) => {
    const updatedStats = cloneDeep(locationStats);

    // Convert the locationStats array into a lookup object for easier access
    const statsLookup = updatedStats.reduce((acc, stat) => {
      acc[stat.locationId] = stat;
      return acc;
    }, {} as Record<number, { locationId: number; name: string; netSales: number; commissions: number; grossSales: number }>);

    // Aggregate data into the statsLookup object
    orders.forEach((order) => {
      const { locationId } = order;
      const { netSales, grossSales } = order.summary;
      const commissions = parseFloat((Number(order.commission || 0)).toFixed(2)); // Ensure two decimal places

      // Find the location by locationId
      const location = locationsByPermissions?.find((loc) => loc.id === locationId);

      // Skip if the locationId doesn't exist in locations
      if (!location) return;

      // Aggregate data for matching locations
      if (statsLookup[locationId]) {
        statsLookup[locationId].netSales += netSales;
        statsLookup[locationId].commissions = parseFloat(
          (statsLookup[locationId].commissions + commissions).toFixed(2),
        ); // Update with two decimals
        statsLookup[locationId].grossSales += grossSales;
      } else {
        statsLookup[locationId] = {
          name: location.name,
          netSales,
          commissions,
          grossSales,
          locationId,
        };
      }
    });

    // Convert the updated statsLookup object back into an array
    return Object.values(statsLookup);
  };

  const getOrderTotalsByLocation = (orders: Array<TAssignOrder>) => {
    const totalsByLocation: Record<string, {
      salesTotal: number;
      totalOrders: number;
      productsTotal: number;
      commissionsTotal: number;
    }> = {};

    orders.forEach((order) => {
      const locationId = String(order.locationId);

      if (!totalsByLocation[locationId]) {
        totalsByLocation[locationId] = {
          salesTotal: 0,
          totalOrders: 0,
          productsTotal: 0,
          commissionsTotal: 0,
        };
      }

      totalsByLocation[locationId].salesTotal += order.summary.netSales || 0;
      totalsByLocation[locationId].totalOrders += 1;
      totalsByLocation[locationId].productsTotal += order.lineItems?.length || 0;
      totalsByLocation[locationId].commissionsTotal += parseFloat(order.commission as string) || 0;
    });

    return totalsByLocation;
  };

  const calculateSalesAndCommissions = (ordersByMember: { [key: string]: Array<TAssignOrder> }) => {
    const salesAndCommissionsByMember = cloneDeep(employeeRates);

    Object.entries(ordersByMember).forEach(([memberKey, orders]) => {
      orders.forEach((order) => {
        const { locationId } = order;

        if (!salesAndCommissionsByMember[locationId]) {
          salesAndCommissionsByMember[locationId] = {
            salesByEmployee: {},
            commissionByEmployee: {},
          };
        }

        const locationData = salesAndCommissionsByMember[locationId];

        if (!locationData.salesByEmployee[memberKey]) {
          locationData.salesByEmployee[memberKey] = 0;
        }
        if (!locationData.commissionByEmployee[memberKey]) {
          locationData.commissionByEmployee[memberKey] = 0;
        }

        locationData.salesByEmployee[memberKey] += order.summary.netSales;
        locationData.commissionByEmployee[memberKey] += Number(order.commission || 0);
      });
    });

    return salesAndCommissionsByMember;
  };

  const calculateSalesByChannel = (
    orders: Array<TAssignOrder>,
  ) => {
    const locationNamesById = Object.fromEntries(
      locationsWithColors?.map((location) => [location.id, location.name]),
    );

    const validLocationIds = new Set(locationsWithColors.map((location) => location.id));

    const dataByLocation: Record<number, {
      seriesData: Array<{ name: string; data: number[] }>;
      sourceNames: string[] }> = cloneDeep(channelData);

    orders.forEach((order) => {
      const { sourceName, locationId, summary } = order;

      if (!validLocationIds.has(locationId)) return;

      if (!dataByLocation[locationId]) {
        dataByLocation[locationId] = { seriesData: [], sourceNames: [] };
      }

      const locationData = dataByLocation[locationId];

      if (!locationData.sourceNames.includes(sourceName)) {
        locationData.sourceNames.push(sourceName);
      }

      let series = locationData.seriesData.find((s) => s.name === locationNamesById[locationId]);
      if (!series) {
        series = { name: locationNamesById[locationId] || 'unassigned', data: Array(locationData.sourceNames.length).fill(0) };
        locationData.seriesData.push(series);
      }

      const sourceIndex = locationData.sourceNames.indexOf(sourceName);
      series.data[sourceIndex] = (series.data[sourceIndex] || 0) + summary.netSales;
    });

    return dataByLocation;
  };

  const calcOrdersByHours = (orders: Array<TAssignOrder>) => {
    const ordersByLocationAndHour: Record<string, Record<string, Array<any>>> = {};

    orders.forEach((order) => {
      const {
        locationId, member, processedAt, summary, id: orderId,
      } = order;

      if (!locationId) {
        return;
      }

      const hour = moment.tz(processedAt, shop?.shopTimezone).hour();
      const hourKey = `${hour}:00-${hour + 1}:00`;

      if (!ordersByLocationAndHour[locationId]) {
        ordersByLocationAndHour[locationId] = {};
      }

      if (!ordersByLocationAndHour[locationId][hourKey]) {
        ordersByLocationAndHour[locationId][hourKey] = [];
      }

      const existingOrders = ordersByLocationAndHour[locationId][hourKey];
      const isOrderExists = existingOrders.some(
        (existingOrder) => existingOrder.orderId === orderId,
      );

      if (!isOrderExists) {
        ordersByLocationAndHour[locationId][hourKey].push({
          orderId,
          hour: processedAt,
          member: member as TMember,
          netSales: summary.netSales.toFixed(2),
        });
      }
    });

    return ordersByLocationAndHour;
  };

  const ordersByLocation = (orders: Array<TAssignOrder>) => {
    // Initialize an empty object to store orders grouped by location
    const ordersByLocationData: Record<string, TAssignOrder[]> = {};

    orders.forEach((order) => {
      const locationId = String(order.locationId);

      if (!locationId) {
        return;
      }

      if (!ordersByLocationData[locationId]) {
        ordersByLocationData[locationId] = [];
      }

      ordersByLocationData[locationId].push(order);
    });

    return ordersByLocationData;
  };

  const calculateOrdersByMember = (orders: Array<TAssignOrder>) => {
    const ordersByMember: Record<string, TAssignOrder[]> = {};

    orders.forEach((order) => {
      let assignedKey = '';

      const memberByShopifyId = members.find((member) => String(member.shopifyId) === String(order.userId));
      if (memberByShopifyId) {
        assignedKey = memberByShopifyId.email;
      } else {
        const orderTags = order.tags?.map((tag) => tag.toLowerCase()) || [];

        const memberByCommissionTag = members.find((member) => member.commissionTags?.some((tag) => orderTags.includes(tag.toLowerCase())));

        if (memberByCommissionTag) {
          assignedKey = memberByCommissionTag.email;
        }
      }

      if (!assignedKey) return;

      if (!ordersByMember[assignedKey]) {
        ordersByMember[assignedKey] = [];
      }

      ordersByMember[assignedKey].push(order);
    });

    return ordersByMember;
  };

  const getStopperSummary = (
    orders: TAssignOrder[],
    timesheets?: Array<TTimesheetsList>,
  ) => {
    const prevResult = deepClone(promotersTotal) as Array<TPromoterTotal>;
    const timezone = shop?.shopTimezone;

    const sheetsRecords = timesheets ?? sheets;

    const summaryMap = new Map<string, typeof prevResult[0]>();
    // eslint-disable-next-line no-restricted-syntax
    for (const item of prevResult) {
      summaryMap.set(item.name, {
        ...item,
        days: { ...item.days },
      });
    }

    // eslint-disable-next-line no-restricted-syntax
    for (const member of members) {
      const memberShifts = sheetsRecords?.find((sheet) => sheet.personnel.email === member.email && member.isPromoterCommission);

      const memberSummary = summaryMap.get(member.name) || {
        name: member.name,
        totalCommissions: 0,
        amount: member?.payment?.paymentAmount as number,
        currency: member?.payment?.paymentCurrencyId as number,
        days: {},
      };

      // eslint-disable-next-line no-restricted-syntax
      for (const shift of memberShifts?.shifts || []) {
        const shiftStart = moment.tz(shift.start, timezone);
        const shiftEnd = moment.tz(shift.end, timezone);
        const dayKey = shiftStart.format('YYYY-MM-DD');
        const startStr = shiftStart.format();
        const endStr = shiftEnd.format();
        const durationHours = moment.duration(shiftEnd.diff(shiftStart)).asHours();

        const shiftLocationId = shift.location?.id;
        const shiftLocationName = shift.location?.name;

        // eslint-disable-next-line no-continue
        if (!shiftLocationId) continue;

        const shiftOrders = orders.filter((order) => order.locationId === shiftLocationId
          && isInRangeMoment(shift.start, shift.end, order.processedAt, timezone));

        if (!memberSummary.days[dayKey]) {
          memberSummary.days[dayKey] = {
            totalHours: 0,
            start: startStr,
            end: endStr,
            totalOrders: 0,
            totalNetSales: 0,
            commissions: 0,
            locationName: shiftLocationName,
            locationId: shiftLocationId,
            shiftOrders: [],
          };
        }

        const day = memberSummary.days[dayKey];

        const existingOrderIds = new Set(day.shiftOrders.map((o) => o.id));
        const newOrders = shiftOrders.filter((o) => !existingOrderIds.has(o.id));

        const totalNetSales = newOrders.reduce(
          (sum, order) => sum + (order.summary?.netSales || 0),
          0,
        );

        const newOrdersLineItems = newOrders.flatMap((order) => order.lineItems || []);

        const commissionsData = calculateOrderCommissionByPlan(totalNetSales, member.commissionPlan, newOrdersLineItems);
        const commissionForNewOrders = commissionsData.totalCommission;

        if (moment(startStr).isBefore(moment(day.start))) {
          day.start = startStr;
        }
        if (moment(endStr).isAfter(moment(day.end))) {
          day.end = endStr;
        }

        day.totalHours += durationHours;
        day.totalOrders += newOrders.length;
        day.totalNetSales += totalNetSales;
        day.commissions += commissionForNewOrders;
      }

      memberSummary.totalCommissions = Object.values(memberSummary.days).reduce(
        (sum, day) => sum + (Number(day.commissions) || 0),
        0,
      );

      summaryMap.set(member.name, memberSummary);
    }

    return Array.from(summaryMap.values()).map((member) => ({
      ...member,
      days: Object.values(member.days).sort((a, b) => {
        if (a.locationId < b.locationId) return -1;
        if (a.locationId > b.locationId) return 1;
        return 0;
      }),
    }));
  };

  return {
    aggregateDataByLocation,
    getOrderTotalsByLocation,
    calculateSalesAndCommissions,
    calculateSalesByChannel,
    calcOrdersByHours,
    ordersByLocation,
    calculateOrdersByMember,
    getStopperSummary,
  };
}
